// ABOUTME: Material UI dashboard card component showing weekly 1:1 time tracking with age-based goal comparisons
// ABOUTME: Uses Recharts for age range visualization and displays current week vs historical average data

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Legend,
  Cell,
  ReferenceArea
} from 'recharts';
import { getMobileChartMargins, MOBILE_BREAKPOINT_QUERY } from '../../utils/mobileOptimization';
import { filterDevelopmentalRanges, getAgeLabel, getFirstName } from '../../utils/developmentalRangeUtils';

const OneOnOneTimeTrackerCard = ({ data, error = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY);
  const chartMargins = getMobileChartMargins('composedChart', isMobile);

  // Define cyan colors for 1:1 time
  const cyanColors = {
    main: '#00bcd4',      // cyan 500
    light: '#4dd0e1',     // cyan 300
    dark: '#0097a7'       // cyan 700
  };

  // Loading state
  if (!data && !error) {
    return (
      <Card role="region" aria-label="1:1 time tracker">
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card role="region" aria-label="1:1 time tracker">
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const {
    currentWeek,
    historicalAverage,
    developmentalRanges,
    childName,
    childAge
  } = data;

  // Filter developmental ranges to always show exactly 3 ranges
  const { filteredRanges, showDataInColumn } = filterDevelopmentalRanges(
    developmentalRanges,
    childAge,
    childName,
    'minPerWeek'
  );

  // Prepare chart data from filtered developmental ranges
  const chartData = filteredRanges.map((range, index) => {
    const isCustomChildRange = range.isCustomChildRange === true;
    const shouldShowData = index === showDataInColumn;

    // For single-value goals (where min === max), show a range of ±0.5 for visual clarity
    const goalValue = range.minPerWeek;
    const isSingleValueGoal = range.minPerWeek === range.maxPerWeek;

    return {
      ageRange: getAgeLabel(range),
      goalMin: isCustomChildRange ? null : (isSingleValueGoal ? goalValue - 0.5 : range.minPerWeek),
      goalMax: isCustomChildRange ? null : (isSingleValueGoal ? goalValue + 0.5 : range.maxPerWeek),
      thisWeekDays: shouldShowData ? currentWeek.daysWithSessions : null,
      isCurrentAge: range.isCurrentAge,
      isCustomChildRange: isCustomChildRange,
      goalFill: range.isCurrentAge ? cyanColors.light : theme.palette.grey[300]
    };
  });

  const maxChartValue = Math.max(
    ...filteredRanges.map(r => r.minPerWeek),
    historicalAverage,
    currentWeek.daysWithSessions,
    7 // Max possible days per week
  );

  // Format historical average to 1 decimal place
  const formattedAverage = parseFloat(historicalAverage.toFixed(1));

  // Get first name and age goal text
  const firstName = getFirstName(childName);
  const currentAgeRange = filteredRanges.find(r => r.isCurrentAge);
  const ageGoalText = currentAgeRange
    ? (currentAgeRange.isCustomChildRange ? 'No goal for this age' : `${currentAgeRange.minPerWeek} days per week`)
    : 'N/A';

  // Singular vs plural text
  const daysText = currentWeek.daysWithSessions === 1 ? 'Day' : 'Days';

  return (
    <Card role="region" aria-label="1:1 time tracker">
      <CardContent>
        {/* Card Title */}
        <Typography variant="h5" component="h2" gutterBottom textAlign="center" sx={{ fontWeight: 'bold', mb: 3 }}>
          Weekly 1:1 Time vs Developmental Averages
        </Typography>

        {/* 50/50 Split Layout */}
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} sx={{ mb: 3 }}>
          {/* Left Side - Current Week Count */}
          <Box flex={1} textAlign="center" sx={{ pr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              This Week:
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <Typography
                variant="h2"
                component="div"
                sx={{ fontWeight: 'bold', fontSize: { xs: '2rem', sm: '3rem' }, color: cyanColors.main }}
              >
                {currentWeek.daysWithSessions}
              </Typography>
              <Typography
                variant="h5"
                color="textSecondary"
                sx={{ alignSelf: 'center', textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {daysText}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
              Day {currentWeek.daysIntoWeek} of 7
            </Typography>
          </Box>

          {/* Right Side - Chips */}
          <Box flex={1} display="flex" flexDirection="column" justifyContent="center" gap={2} sx={{ pl: { xs: 0, sm: 2 } }}>
            {/* Position 1: Age Average Chip */}
            <Chip
              data-testid="chip-age-average"
              label={`Avg. for Age: ${ageGoalText}`}
              variant="filled"
              size="medium"
              sx={{
                fontWeight: 'bold',
                backgroundColor: theme.palette.grey[300],
                color: theme.palette.grey[800],
                border: `1px solid ${theme.palette.grey[600]}`
              }}
            />

            {/* Position 2: 12-Week Average Chip */}
            <Chip
              data-testid="chip-12-week-average"
              label={`${firstName} 12 Week Avg: ${formattedAverage}`}
              variant="filled"
              size="medium"
              sx={{
                fontWeight: 'bold',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                border: `1px solid ${theme.palette.primary.dark}`
              }}
            />

            {/* Position 3: This Week Chip */}
            <Chip
              data-testid="chip-this-week"
              label={`${firstName} This Week: ${currentWeek.daysWithSessions}`}
              variant="filled"
              size="medium"
              sx={{
                fontWeight: 'bold',
                backgroundColor: cyanColors.main,
                color: '#ffffff',
                border: `1px solid ${cyanColors.dark}`
              }}
            />
          </Box>
        </Box>

        {/* Chart */}
        <Box sx={{ width: '100%', height: { xs: 200, sm: 250 }, mb: 1 }}>
          <ResponsiveContainer>
            <ComposedChart
              data={chartData}
              margin={chartMargins}
              barCategoryGap={0}
              aria-label="1:1 time by age group chart"
            >
              <XAxis
                dataKey="ageRange"
                tick={{ fontSize: 12, textAnchor: 'middle' }}
                axisLine={{ stroke: theme.palette.grey[800], strokeWidth: 2 }}
                interval={0}
              />
              <YAxis
                label={{
                  value: 'Days per Week',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12 }
                }}
                domain={[0, maxChartValue + 1]}
                tick={{ fontSize: 12 }}
                tickCount={maxChartValue + 2}
                interval={0}
                axisLine={{ stroke: theme.palette.grey[800], strokeWidth: 2 }}
              />

              {/* Developmental goal backgrounds */}
              {chartData.map((entry, index) => {
                // Only show ReferenceArea if there's a goal (skip child's age column if outside ranges)
                if (entry.goalMin === null || entry.goalMax === null) {
                  return null;
                }

                return (
                  <ReferenceArea
                    key={`range-${index}`}
                    x1={entry.ageRange}
                    x2={entry.ageRange}
                    y1={entry.goalMin}
                    y2={entry.goalMax}
                    fill={entry.goalFill}
                    fillOpacity={0.6}
                    stroke={theme.palette.grey[600]}
                    strokeWidth={1}
                  />
                );
              })}

              {/* This week's count bar */}
              <Bar
                dataKey="thisWeekDays"
                name="This Week"
                radius={[8, 8, 0, 0]}
                barSize={40}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.isCurrentAge ? cyanColors.dark : 'transparent'}
                  />
                ))}
              </Bar>

              {/* 12-week average reference line */}
              <ReferenceLine
                y={historicalAverage}
                stroke={theme.palette.primary.main}
                strokeDasharray="5 5"
                strokeWidth={2}
                label={{
                  value: `12-Week Avg: ${historicalAverage.toFixed(1)}`,
                  position: 'insideTopRight',
                  fill: theme.palette.primary.main,
                  fontSize: 11
                }}
              />

              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                content={() => (
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: cyanColors.light, opacity: 0.3 }} />
                      <Typography variant="caption">Goal</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: cyanColors.dark }} />
                      <Typography variant="caption">This Week</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 20, height: 2, backgroundColor: theme.palette.primary.main, borderStyle: 'dashed' }} />
                      <Typography variant="caption">12-Week Avg</Typography>
                    </Box>
                  </Box>
                )}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>

        {/* Footer */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
          1:1 time sessions are tracked in your daily behavior log
        </Typography>
      </CardContent>
    </Card>
  );
};

export default OneOnOneTimeTrackerCard;
